import { Dayjs } from 'dayjs'
import { immer } from 'zustand/middleware/immer'

import { isSame } from '~/astra'
import { Location } from '~/storm'

import { Slice } from '../store'
import { DrawerActions } from './types'

export type LocationDateSelection = {
    type: 'locationDate'
    date: Dayjs
    locationId: Location['id']
}

export type ScheduleDateSelection = {
    type: 'scheduleDate'
    date: Dayjs
}

export type SidebarSelection = LocationDateSelection | ScheduleDateSelection
export type SidebarSelectionType = SidebarSelection['type']

export type TabValue = 'postOp' | 'comments'
type State = {
    selection: SidebarSelection | null
    isDrawerOpen: boolean
    activeDrawerTab: TabValue
    // keeps track of the open state of the "page"-drawer state
    // such that we can close the drawer if user just opened eg. for comments
    closePageDrawerOnClose: boolean | null
}

const initialState: State = {
    selection: null,
    isDrawerOpen: false,
    activeDrawerTab: 'comments',
    closePageDrawerOnClose: null,
}

type Actions = {
    actions: {
        setSelection: (selection: SidebarSelection | null) => void
        clearSelection: () => void
        updateSelectionDate: (date: Dayjs) => void
    } & Omit<DrawerActions<TabValue, SidebarSelection>, 'openDrawer'> & {
            openDrawer: (selection: SidebarSelection, closePageDrawerOnClose?: boolean) => void
        }
}

export type SelectionDrawerSlice = {
    selectionDrawer: State & Actions
}

export const createSelectionDrawerSlice: Slice<SelectionDrawerSlice> = immer(set => ({
    selectionDrawer: {
        ...initialState,
        actions: {
            setSelection: (selection: SidebarSelection | null) => {
                set(state => {
                    state.selectionDrawer.selection = selection
                })
            },
            clearSelection: () => {
                set(state => {
                    state.selectionDrawer.selection = null
                })
            },
            // Update the selected date without touching the drawer's open/close state.
            updateSelectionDate: (date: Dayjs) => {
                set(state => {
                    if (state.selectionDrawer.selection === null) return
                    state.selectionDrawer.selection.date = date
                })
            },
            openDrawer: (selection: SidebarSelection, closePageDrawerOnClose?: boolean) => {
                set(state => {
                    state.selectionDrawer.selection = selection
                    state.selectionDrawer.isDrawerOpen = true
                    state.selectionDrawer.closePageDrawerOnClose = closePageDrawerOnClose ?? null
                })
            },
            closeDrawer: () => {
                set(state => {
                    state.selectionDrawer.isDrawerOpen = false
                    state.selectionDrawer.selection = null
                    state.selectionDrawer.closePageDrawerOnClose = null
                })
            },
            toggleDrawer: () => {
                set(state => {
                    state.selectionDrawer.isDrawerOpen = !state.selectionDrawer.isDrawerOpen
                })
            },
            setActiveDrawerTab: (tabValue: TabValue) => {
                set(state => {
                    state.selectionDrawer.activeDrawerTab = tabValue
                })
            },
        },
    },
}))

export const isLocationDateSelection = (selection: SidebarSelection): selection is LocationDateSelection => selection.type === 'locationDate'
export const isScheduleDateSelection = (selection: SidebarSelection): selection is ScheduleDateSelection => selection.type === 'scheduleDate'

export const isSelectionForScheduleDate = (selection: SidebarSelection | undefined | null, date: Dayjs): boolean =>
    !!selection && isScheduleDateSelection(selection) && isSame(selection.date, date, 'day')
